import React from 'react';
import DatePicker from 'react-datepicker';
import uuid from 'uuid/v4';

import 'react-datepicker/dist/react-datepicker.css';
import '../forms.css';
import './date.css';
import { systemText } from '../../../helpers/string';
import FormLabel from '../formLabel';

const DateControl = props => {
	const {
		extraProps,
		inline,
		onChange,
		value,
		label,
		mandatory,
		help,
		error,
		warning,
		id,
		disabled,
		placeholder,
		allowNull,
		className,
		readonly,
		showTime,
		bypassDefault,
		...rest
	} = props;

	const guid = uuid();

	const [local, setLocal] = React.useState(null);
	const [hour, setHour] = React.useState('');
	const [minute, setMinute] = React.useState('');

	React.useEffect(() => {
		try {
			if (!bypassDefault && !local) {
				var date = value ? new Date(value) : null;
				if (date) {
					setLocal(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
					if (showTime) {
						setHour(date.getHours());
						setMinute(date.getMinutes());
					}
				} else {
					setLocal(null);
				}
			}
		} catch {
			// invalid date
		}
	}, [value]);

	React.useEffect(() => {
		const pad = x => x.toString().padStart(2, '0');

		if (local) {
			if (showTime) {
				local.setHours(parseInt(hour, 10));
				local.setMinutes(parseInt(minute, 10));
				onChange?.(local.toISOString());
			} else {
				onChange?.(`${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}`);
			}
		}
	}, [local, hour, minute]);

	const clear = () => {
		setLocal(null);
		onChange?.(null);
	};

	return (
		<div
			key={id}
			data-testid={rest['data-testid']}
			id={id}
			className={`form-group ${error ? 'has-error' : warning ? 'has-warning' : ''}`}
		>
			<FormLabel type="date" {...props} htmlFor={`date-${id || guid}`} />
			<div className="flex flex-center">
				<div className="input-group" style={{ position: 'relative' }}>
					{!inline && (
						<span className="input-group-addon">
							<i className="fa fa-calendar" />
						</span>
					)}
					<DatePicker
						{...extraProps}
						id={`date-${id || guid}`}
						inline={inline}
						disabled={disabled}
						placeholderText={placeholder}
						className={`form-control ${className}`}
						dateFormat={'dd MMM yyyy'}
						selected={local}
						onSelect={x => {
							setLocal(x);
						}}
						// onChange={trackChange}
						// onBlur={trackBlur}
						readOnly={readonly}
						popperModifiers={{
							offset: {
								enabled: true,
								offset: '0px, 0px',
							},
							preventOverflow: {
								enabled: true,
								escapeWithReference: false,
								boundariesElement: 'scrollParent',
							},
						}}
					/>
				</div>
				{showTime && (
					<div className="input-group flex flex-center ml10" style={{ position: 'relative', top: '-2px' }}>
						<span className="input-group-addon">
							<i className="fa fa-clock" />
						</span>
						<input
							type="number"
							disabled={disabled}
							min={0}
							max={23}
							maxLength={2}
							value={hour.toString().padStart(2, '0')}
							onChange={x => setHour(x.target.value.toString().padStart(2, '0'))}
							className="form-control time-control"
						/>
						<span className="input-group-addon narrow">:</span>
						<input
							type="number"
							disabled={disabled}
							min={0}
							max={59}
							step={5}
							maxLength={2}
							value={minute.toString().padStart(2, '0')}
							onChange={x => setMinute(x.target.value.toString().padStart(2, '0'))}
							className="form-control time-control"
						/>
					</div>
				)}
			</div>
			{allowNull && value && (
				<div>
					<a className="pointer" onClick={clear}>
						{systemText('General/Controls/Date/Clear')}
					</a>
				</div>
			)}
			{error && <span className="help-block">{typeof error === 'function' ? error() : error}</span>}
			{warning && <span className="help-block">{typeof warning === 'function' ? warning() : warning}</span>}
		</div>
	);
};

export default DateControl;
